import dayjs from 'dayjs';
import { devtools, persist } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { Stages } from '../../api/process-client/process-client.type';
import type { Task } from '../../api/tasks-client/task-client.type';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { isLoggingEnabled, logger } from '../logger/logger';
import type {
  CachedProcessScope,
  EventScope,
  ProcessScope,
  ProcessWorkflow,
  TaskSearchHistoryByPortfolio,
} from './process-store.type';

export interface ProcessStore {
  // Left (primary) section state
  scope: ProcessScope;
  setScope: (newScope: Partial<ProcessScope>) => void;
  stages: Stages[];
  setStages: (stages: Stages[]) => void;
  workflow: ProcessWorkflow | null;
  setWorkflow: (workflow: ProcessWorkflow | null) => void;
  eventScope: EventScope;
  setEventScope: (eventScope: EventScope) => void;

  // Comparison view properties
  isComparisonView: boolean;
  setIsComparisonView: (isComparisonView: boolean) => void;
  rightScope: ProcessScope;
  setRightScope: (newScope: Partial<ProcessScope>) => void;
  rightStages: Stages[];
  setRightStages: (stages: Stages[]) => void;
  rightWorkflow: ProcessWorkflow | null;
  setRightWorkflow: (workflow: ProcessWorkflow | null) => void;
  rightEventScope: EventScope;
  setRightEventScope: (eventScope: EventScope) => void;

  // Recent Task-search picks, shared by both graphs and keyed by portfolio.
  // Deliberately a sibling of `scope`/`rightScope`, not a field of either:
  // there is one history, and scope identity drives data fetching.
  taskSearchHistoryByPortfolio: TaskSearchHistoryByPortfolio;
  addTaskSearchHistoryEntry: (portfolioId: string, task: Task) => void;
  removeTaskSearchHistoryEntry: (portfolioId: string, taskId: string) => void;
  clearTaskSearchHistory: (portfolioId: string) => void;
}

/** Maximum number of recent Task-search picks kept per portfolio. */
const TASK_SEARCH_HISTORY_LIMIT = 10;

/**
 * Returns the history list with `task` moved to the front, deduplicated by id and
 * capped at the limit. Pure, so the store action and its tests share one definition.
 *
 * The stored copy drops the task's `description`: it is the one free-text field,
 * nothing on the Process page reads it from the selected task, and the whole map
 * is re-serialised to localStorage on every process-store update.
 *
 * @param {Task[]} entries - The current list for a portfolio, most recent first
 * @param {Task} task - The task the user just picked
 * @returns {Task[]} A new list with the pick first and at most the limit of entries
 */
function pushTaskSearchHistoryEntry(entries: Task[], task: Task): Task[] {
  const withoutDuplicate = entries.filter((entry) => entry.id !== task.id);
  return [{ ...task, description: '' }, ...withoutDuplicate].slice(0, TASK_SEARCH_HISTORY_LIMIT);
}

/**
 * Checks that a persisted value has every field the Task field reads when it
 * renders or re-applies a recent pick: a string `id` and `name`, and a `url`
 * that is a string or absent.
 *
 * @param {unknown} entry - One element of a persisted history list
 * @returns {boolean} True when the entry can be rendered and re-applied safely
 */
function isPersistedTask(entry: unknown): entry is Task {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const { id, name, url } = entry as Partial<Record<keyof Task, unknown>>;
  return (
    typeof id === 'string' && typeof name === 'string' && (url === undefined || url === null || typeof url === 'string')
  );
}

/**
 * Coerces whatever was persisted under the history key into a usable map.
 *
 * The persisted blob is user-editable and survives schema changes, so a value of
 * the wrong type (a string, an array, `null`) or a list holding non-task junk must
 * degrade to "no history" rather than crash the first render of the dropdown.
 *
 * @param {unknown} value - The raw value read from the persisted store, if any
 * @returns {TaskSearchHistoryByPortfolio} A map whose lists hold only objects with a string `id`
 */
function sanitizeTaskSearchHistory(value: unknown): TaskSearchHistoryByPortfolio {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }

  const sanitized: TaskSearchHistoryByPortfolio = {};
  for (const [portfolioId, entries] of Object.entries(value as Record<string, unknown>)) {
    if (!Array.isArray(entries)) {
      continue;
    }
    const seenIds = new Set<string>();
    const tasks: Task[] = [];
    for (const entry of entries) {
      if (isPersistedTask(entry) && !seenIds.has(entry.id)) {
        seenIds.add(entry.id);
        tasks.push(entry);
      }
    }
    sanitized[portfolioId] = tasks.slice(0, TASK_SEARCH_HISTORY_LIMIT);
  }

  return sanitized;
}

const defaultCachedScope: CachedProcessScope = {
  portfolio: null,
  team: null,
  board: null,
  epic: null,
  task: null,
  workflow: null,
};

const defaultEventScope: EventScope = {
  showEventGraph: false,
  statuses: [],
  statusGroup: null,
  selectedEdgeId: null,
};

// Get the cached scope from localStorage or use default values
const getCachedScope = (): CachedProcessScope => {
  try {
    const storedScope = localStorage.getItem('process-scope');
    return storedScope ? JSON.parse(storedScope) : defaultCachedScope;
  } catch (error) {
    console.error('Error parsing cached process scope:', error);
    return defaultCachedScope;
  }
};

const useProcessStoreUpdate = create<ProcessStore>()(
  devtools(
    persist(
      logger((set) => {
        const cachedScope = getCachedScope();

        return {
          // Left (primary) section state
          scope: {
            ...cachedScope,
            startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
            endDate: dayjs().toISOString(),
            taskTypes: null,
            useWorkflow: false,
            useGroups: true,
            workPeriod: null,
          },
          setScope: (newScope: Partial<ProcessScope>) => {
            set(
              (state) => {
                let updatedCachedScope = { ...state.scope };
                let hasChanged = false;

                // Handle portfolio changes
                if ('portfolio' in newScope) {
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    portfolio: newScope.portfolio === undefined ? null : newScope.portfolio,
                    team: null,
                    board: null,
                    epic: null,
                    task: null,
                  };
                  hasChanged = true;
                }

                // Handle team changes (including setting to null)
                if ('team' in newScope) {
                  const newTeam = newScope.team === undefined ? null : newScope.team;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    team: newTeam,
                    // Clear dependent fields when team is null
                    board: newTeam ? updatedCachedScope.board : null,
                    epic: newTeam ? updatedCachedScope.epic : null,
                    task: newTeam ? updatedCachedScope.task : null,
                  };
                  hasChanged = true;
                }

                // Handle board changes (including setting to null)
                if ('board' in newScope) {
                  const newBoard = newScope.board === undefined ? null : newScope.board;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    board: newBoard,
                    // Clear epic and task when board is null
                    epic: newBoard ? updatedCachedScope.epic : null,
                    task: newBoard ? updatedCachedScope.task : null,
                  };
                  hasChanged = true;
                }

                // Handle epic changes (including setting to null)
                if ('epic' in newScope) {
                  const newEpic = newScope.epic === undefined ? null : newScope.epic;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    epic: newEpic,
                    // Clear task when epic changes
                    task: null,
                  };
                  hasChanged = true;
                }

                // Handle task changes (including setting to null)
                if ('task' in newScope) {
                  const newTask = newScope.task === undefined ? null : newScope.task;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    task: newTask,
                    // Clear epic when task is null
                    epic: newTask ? updatedCachedScope.epic : null,
                  };
                  hasChanged = true;
                }

                // Handle workflow changes
                if ('workflow' in newScope) {
                  const newWorkflow = newScope.workflow === undefined ? null : newScope.workflow;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    workflow: newWorkflow,
                  };
                  hasChanged = true;
                }

                // Handle taskType changes - clear task when taskType changes
                if ('taskType' in newScope) {
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    task: null,
                  };
                  hasChanged = true;
                }

                // Store cached scope in localStorage
                if (hasChanged) {
                  const cachedScopeToStore: CachedProcessScope = {
                    portfolio: updatedCachedScope.portfolio,
                    team: updatedCachedScope.team,
                    board: updatedCachedScope.board,
                    epic: updatedCachedScope.epic,
                    task: updatedCachedScope.task,
                    workflow: updatedCachedScope.workflow,
                  };
                  // Guarded: this runs inside the zustand set() updater, so an
                  // unguarded quota error throws out of the state update and blanks
                  // the page. The cache is a convenience, never correctness.
                  try {
                    localStorage.setItem('process-scope', JSON.stringify(cachedScopeToStore));
                  } catch (error) {
                    console.warn('Failed to cache the process scope; continuing without it.', error);
                  }
                }

                // Handle workPeriod changes - clear work period when board is null
                let workPeriodValue = state.scope.workPeriod;
                if ('workPeriod' in newScope) {
                  workPeriodValue = newScope.workPeriod === undefined ? null : newScope.workPeriod;
                } else if ('board' in newScope && newScope.board === null) {
                  // Clear work period if board is being cleared
                  workPeriodValue = null;
                }

                // Handle other fields
                return {
                  scope: {
                    ...updatedCachedScope,
                    startDate: newScope.startDate !== undefined ? newScope.startDate : state.scope.startDate,
                    endDate: newScope.endDate !== undefined ? newScope.endDate : state.scope.endDate,
                    taskTypes: newScope.taskTypes !== undefined ? newScope.taskTypes : state.scope.taskTypes,
                    useWorkflow: newScope.useWorkflow !== undefined ? newScope.useWorkflow : state.scope.useWorkflow,
                    useGroups: newScope.useGroups !== undefined ? !!newScope.useGroups : state.scope.useGroups,
                    workPeriod: workPeriodValue,
                  },
                };
              },
              false,
              '[Process Store] setScope',
            );
          },
          stages: [],
          setStages: (stages: Stages[]) => set({ stages }, false, '[Process Store] setStages'),
          workflow: null,
          setWorkflow: (workflow: ProcessWorkflow | null) => set({ workflow }, false, '[Process Store] setWorkflow'),
          eventScope: defaultEventScope,
          setEventScope: (eventScope: EventScope) => set({ eventScope }, false, '[Process Store] setEventScope'),

          // Comparison view properties
          isComparisonView: false,
          setIsComparisonView: (isComparisonView: boolean) =>
            set({ isComparisonView }, false, '[Process Store] setIsComparisonView'),
          rightScope: {
            ...defaultCachedScope,
            startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
            endDate: dayjs().toISOString(),
            taskTypes: null,
            useWorkflow: false,
            useGroups: true,
            workPeriod: null,
          },
          setRightScope: (newScope: Partial<ProcessScope>) => {
            set(
              (state) => {
                let updatedRightScope = { ...state.rightScope };

                // Handle portfolio changes
                if ('portfolio' in newScope) {
                  updatedRightScope = {
                    ...updatedRightScope,
                    portfolio: newScope.portfolio === undefined ? null : newScope.portfolio,
                    team: null,
                    board: null,
                    epic: null,
                    task: null,
                  };
                }

                // Handle team changes (including setting to null)
                if ('team' in newScope) {
                  const newTeam = newScope.team === undefined ? null : newScope.team;
                  updatedRightScope = {
                    ...updatedRightScope,
                    team: newTeam,
                    // Clear dependent fields when team is null
                    board: newTeam ? updatedRightScope.board : null,
                    epic: newTeam ? updatedRightScope.epic : null,
                    task: newTeam ? updatedRightScope.task : null,
                  };
                }

                // Handle board changes (including setting to null)
                if ('board' in newScope) {
                  const newBoard = newScope.board === undefined ? null : newScope.board;
                  updatedRightScope = {
                    ...updatedRightScope,
                    board: newBoard,
                    // Clear epic and task when board is null
                    epic: newBoard ? updatedRightScope.epic : null,
                    task: newBoard ? updatedRightScope.task : null,
                  };
                }

                // Handle epic changes (including setting to null)
                if ('epic' in newScope) {
                  const newEpic = newScope.epic === undefined ? null : newScope.epic;
                  updatedRightScope = {
                    ...updatedRightScope,
                    epic: newEpic,
                    // Clear task when epic changes
                    task: null,
                  };
                }

                // Handle task changes (including setting to null)
                if ('task' in newScope) {
                  const newTask = newScope.task === undefined ? null : newScope.task;
                  updatedRightScope = {
                    ...updatedRightScope,
                    task: newTask,
                    // Clear epic when task is null
                    epic: newTask ? updatedRightScope.epic : null,
                  };
                }

                // Handle workflow changes
                if ('workflow' in newScope) {
                  const newWorkflow = newScope.workflow === undefined ? null : newScope.workflow;
                  updatedRightScope = {
                    ...updatedRightScope,
                    workflow: newWorkflow,
                  };
                }

                // Handle taskType changes - clear task when taskType changes
                if ('taskType' in newScope) {
                  updatedRightScope = {
                    ...updatedRightScope,
                    task: null,
                  };
                }

                // Handle workPeriod changes - clear work period when board is null
                let workPeriodValue = state.rightScope.workPeriod;
                if ('workPeriod' in newScope) {
                  workPeriodValue = newScope.workPeriod === undefined ? null : newScope.workPeriod;
                } else if ('board' in newScope && newScope.board === null) {
                  // Clear work period if board is being cleared
                  workPeriodValue = null;
                }

                // Handle other fields
                return {
                  rightScope: {
                    ...updatedRightScope,
                    startDate: newScope.startDate !== undefined ? newScope.startDate : state.rightScope.startDate,
                    endDate: newScope.endDate !== undefined ? newScope.endDate : state.rightScope.endDate,
                    taskTypes: newScope.taskTypes !== undefined ? newScope.taskTypes : state.rightScope.taskTypes,
                    useWorkflow:
                      newScope.useWorkflow !== undefined ? newScope.useWorkflow : state.rightScope.useWorkflow,
                    useGroups: newScope.useGroups !== undefined ? !!newScope.useGroups : state.rightScope.useGroups,
                    workPeriod: workPeriodValue,
                  },
                };
              },
              false,
              '[Process Store] setRightScope',
            );
          },
          rightStages: [],
          setRightStages: (rightStages: Stages[]) => set({ rightStages }, false, '[Process Store] setRightStages'),
          rightWorkflow: null,
          setRightWorkflow: (rightWorkflow: ProcessWorkflow | null) =>
            set({ rightWorkflow }, false, '[Process Store] setRightWorkflow'),
          rightEventScope: defaultEventScope,
          setRightEventScope: (rightEventScope: EventScope) =>
            set({ rightEventScope }, false, '[Process Store] setRightEventScope'),

          taskSearchHistoryByPortfolio: {},
          addTaskSearchHistoryEntry: (portfolioId: string, task: Task) =>
            set(
              (state) => ({
                taskSearchHistoryByPortfolio: {
                  ...state.taskSearchHistoryByPortfolio,
                  [portfolioId]: pushTaskSearchHistoryEntry(
                    state.taskSearchHistoryByPortfolio[portfolioId] ?? [],
                    task,
                  ),
                },
              }),
              false,
              '[Process Store] addTaskSearchHistoryEntry',
            ),
          removeTaskSearchHistoryEntry: (portfolioId: string, taskId: string) =>
            set(
              (state) => {
                const entries = state.taskSearchHistoryByPortfolio[portfolioId];
                if (!entries?.some((entry) => entry.id === taskId)) {
                  // Returning the same state object is the only true no-op: a
                  // fresh `{}` still notifies every subscriber and re-persists.
                  return state;
                }
                return {
                  taskSearchHistoryByPortfolio: {
                    ...state.taskSearchHistoryByPortfolio,
                    [portfolioId]: entries.filter((entry) => entry.id !== taskId),
                  },
                };
              },
              false,
              '[Process Store] removeTaskSearchHistoryEntry',
            ),
          clearTaskSearchHistory: (portfolioId: string) =>
            set(
              (state) => {
                if (!(portfolioId in state.taskSearchHistoryByPortfolio)) {
                  return state;
                }
                const { [portfolioId]: _cleared, ...rest } = state.taskSearchHistoryByPortfolio;
                return { taskSearchHistoryByPortfolio: rest };
              },
              false,
              '[Process Store] clearTaskSearchHistory',
            ),
        };
      }),
      {
        name: 'process-storage',
        storage: createSafeJSONStorage(() => localStorage),
        partialize: (state) => ({
          scope: state.scope,
          stages: state.stages,
          workflow: state.workflow,
          eventScope: state.eventScope,
          isComparisonView: state.isComparisonView,
          rightScope: state.rightScope,
          rightStages: state.rightStages,
          rightWorkflow: state.rightWorkflow,
          rightEventScope: state.rightEventScope,
          taskSearchHistoryByPortfolio: state.taskSearchHistoryByPortfolio,
        }),
        version: 1,
        migrate: (persistedState, version) => {
          // A corrupted blob can hold a primitive here; assigning to it would throw
          // and reject the whole hydration.
          const state = (
            typeof persistedState === 'object' && persistedState !== null ? persistedState : {}
          ) as Partial<ProcessStore>;
          if (version < 1) {
            // v1: Task-search history added. Blobs written before it have no
            // such key; give them an empty map so the dropdown reads a list.
            state.taskSearchHistoryByPortfolio = {};
          }
          return state as ProcessStore;
        },
        // `migrate` only runs when the stored version differs, so a current-version
        // blob that was hand-edited or corrupted would skip it. Coerce the history
        // on every rehydration instead; the rest merges exactly as zustand's default.
        merge: (persistedState, currentState) => {
          // Same guard as `migrate`: a primitive here would otherwise spread its
          // characters onto the state as indexed keys.
          const persisted = (
            typeof persistedState === 'object' && persistedState !== null ? persistedState : {}
          ) as Partial<ProcessStore>;
          return {
            ...currentState,
            ...persisted,
            taskSearchHistoryByPortfolio: sanitizeTaskSearchHistory(persisted.taskSearchHistoryByPortfolio),
          };
        },
      },
    ),
    {
      name: 'Bloomfilter - Process Store',
      enabled: isLoggingEnabled,
    },
  ),
);

export { TASK_SEARCH_HISTORY_LIMIT, pushTaskSearchHistoryEntry, sanitizeTaskSearchHistory, useProcessStoreUpdate };
